﻿import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";

import { ArrowLeft } from "lucide-react";

import { useSearchParams, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../../auth/AuthContext";
import { Check, AlertCircle } from "lucide-react";
import { validators, formatters } from "../../utils/validators";
import { LoadingSpinner, ErrorCard } from "../../components/LoadingSpinner";
import { isApiConfigured } from "../../api/axiosClient";
import { userApi } from "../../api/userApi";
import { eftEnrollmentApi } from "../../api/eftEnrollmentApi";
import { useToast } from "../../components/Toast";

const REASONS = ["New enrollment", "Change", "Cancel"];

/*
 * ABA routing-number checksum
 * 9 digits, weights 3-7-1
 */
const abaRouting = (value) => {
  if (!value) {
    return null;
  }

  const digits = String(value).replace(/\D/g, "");

  if (digits.length !== 9) {
    return "Routing number must be 9 digits";
  }

  const sum = [3, 7, 1, 3, 7, 1, 3, 7, 1].reduce(
    (total, weight, index) => total + weight * Number(digits[index]),
    0,
  );

  return sum % 10 === 0
    ? null
    : "Routing number failed the ABA checksum — please re-check it";
};

/*
 * Account number validation
 */
const accountNumber = (value) => {
  if (!value) {
    return null;
  }

  return /^\d{4,17}$/.test(String(value).replace(/\s/g, ""))
    ? null
    : "Account number must be 4–17 digits";
};

/*
 * Date cannot be in the past
 */
const notInPast = (value) => {
  if (!value) {
    return null;
  }

  const today = new Date();

  today.setHours(0, 0, 0, 0);

  return new Date(`${value}T00:00:00`) < today
    ? "Date cannot be in the past"
    : null;
};

/*
 * Required / conditional field marker
 */
const Tag = ({ kind }) => {
  if (kind === "req") {
    return (
      <span className="enroll-req" title="Required" aria-label="required">
        *
      </span>
    );
  }

  if (kind === "cond") {
    return (
      <span
        className="enroll-cond"
        title="Conditional"
        aria-label="conditional"
      >
        ▲
      </span>
    );
  }

  return null;
};

/*
 * Form field component
 */
function F({ label, tag, c = 4, hint, error, children, field }) {
  return (
    <div
      className={`enroll-fg${error ? " has-error" : ""}`}
      style={{
        gridColumn: `span ${c}`,
      }}
      data-field={field}
    >
      <label>
        {label}

        {tag && <Tag kind={tag} />}
      </label>

      {children}

      {error ? (
        <span className="enroll-error" role="alert">
          <AlertCircle size={13} strokeWidth={2.2} />

          {error}
        </span>
      ) : (
        hint && <span className="enroll-hint">{hint}</span>
      )}
    </div>
  );
}

/*
 * Section component
 */
function Section({ title, note, children }) {
  return (
    <div className="card">
      <div className="enroll-sec-head">
        <h3>{title}</h3>

        {note && <span>{note}</span>}
      </div>

      <div className="enroll-grid">{children}</div>
    </div>
  );
}

export default function EFTEnrollment() {
  const { user } = useAuth();
  const toast = useToast();
  const { uid, mode, id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [userTaxIds, setUserTaxIds] = useState([]);
  const [selectedTaxId, setSelectedTaxId] = useState("");
  const [selectedNpi, setSelectedNpi] = useState("");
  const [providerAdminUserId, setProviderAdminUserId] = useState(user.userId);
  // const [selectedLinkTo, setSelectedLinkTo] = useState('');
  const [enrollmentId, setEnrollmentId] = useState("");
  const [npisForSelectedTaxId, setNpisForSelectedTaxId] = useState([]);

  const [pageLoading, setPageLoading] = useState(false);

  const [reason, setReason] = useState("New enrollment");

  const [agentOn, setAgentOn] = useState(false);
  const [showAcct, setShowAcct] = useState(false);
  const [agree, setAgree] = useState(false);

  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState("");

  const counter = useRef(122);

  /* get parameters value when super admin perform change or cancel operation */

  //const id = searchParams.get('id');
  //const mode = searchParams.get('mode');
  //const uid = searchParams.get('uid'); //provider admin user id

  /*
   * Provider information from logged-in user
   */
  const org = user?.orgName || user?.organizationName || "";

  const npi =
    //user?.npi ||
    "";

  const tin =
    //user?.taxId ||
    "";

  const contact = user
    ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
    : "";

  const email = user?.email || "";

  /*
   * Link options
   */
  //const linkOptions = useMemo(
  //    () => [
  //        selectedNpi
  //            ? `NPI—${selectedNpi}`
  //            : 'NPI',

  //        selectedTaxId
  //            ? `TIN—${selectedTaxId}`
  //            : 'TIN'
  //    ],
  //    [
  //        selectedNpi,
  //        selectedTaxId
  //    ]
  //);

  const getLocalDate = (daysToAdd = 0) => {
    const date = new Date();
    date.setDate(date.getDate() + daysToAdd);

    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, "0"),
      String(date.getDate()).padStart(2, "0"),
    ].join("-");
  };

  /*
   * Form state
   */

  const getDefaultFormValues = () => ({
    // Provider
    legalName: org,
    dba: "",
    tin,
    npi,
    contact,
    email,

    tradingPartnerId: "",
    phone: user?.phone || "",
    fax: "",

    address: user?.address || "",
    city: user?.city || "",
    state: user?.state || "",
    zip: user?.zip || "",

    // Third-party agent
    agentName: "",
    agentPhone: "",
    agentEmail: "",

    // EFT / Bank
    bankName: "",
    routing: "",
    accountType: "Checking",
    account: "",
    // linkTo: linkOptions[0],
    linkTo: "",
    bankPhone: "",

    // Authorization
    effectiveDate: getLocalDate(15),
    signer: contact,
    title: "",
    signature: "",
    signatureDate: getLocalDate(),
    status: "",
    adminNote: "",
  });

  const [f, setF] = useState(() => getDefaultFormValues());

  const linkOptions = useMemo(
    () => [f.npi ? `NPI—${f.npi}` : "NPI", f.tin ? `TIN—${f.tin}` : "TIN"],
    [f.npi, f.tin],
  );

  //const [f, setF] = useState(() => ({

  //    // Provider
  //    legalName: org,
  //    dba: '',
  //    tin,
  //    npi,
  //    contact,
  //    email,

  //    tradingPartnerId: '',
  //    phone: user?.phone || '',
  //    fax: '',

  //    address: user?.address || '',
  //    city: user?.city || '',
  //    state: user?.state || '',
  //    zip: user?.zip || '',

  //    // Third-party agent
  //    agentName: '',
  //    agentPhone: '',
  //    agentEmail: '',

  //    // EFT / Bank
  //    bankName: '',
  //    routing: '',
  //    accountType: 'Checking',
  //    account: '',
  //    linkTo: linkOptions[0],
  //    bankPhone: '',

  //    // Authorization
  //    effectiveDate: '',
  //    signer: contact,
  //    title: '',
  //    signature: '',
  //    signatureDate: ''
  //}));

  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  /*
   * Set field value
   */
  const set = (key) => (value) => {
    setF((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  /*
   * Mark field as touched
   */
  const touch = (key) => () => {
    setTouched((previous) => ({
      ...previous,
      [key]: true,
    }));
  };

  /*
   * Bind input
   */
  const bind = (key, formatter) => ({
    value: f[key] ?? "",

    onChange: (e) =>
      set(key)(formatter ? formatter(e.target.value) : e.target.value),

    onBlur: touch(key),
  });

  /*
   * EFT validation rules
   */
  const rules = useMemo(() => {
    const r = [
      // Provider
      ["legalName", "Provider (legal) name"],

      ["tin", "Federal Tax ID", validators.taxId],

      ["npi", "NPI", validators.npi],

      ["contact", "Contact name"],

      ["email", "Email", validators.email],

      // Contact
      ["phone", "Telephone", validators.phone],

      ["address", "Street address"],

      ["city", "City"],

      ["state", "State", validators.state],

      ["zip", "ZIP", validators.zip],

      // Bank
      ["bankName", "Bank name"],

      ["routing", "Routing number", abaRouting],

      ["accountType", "Account type"],

      ["account", "Account number", accountNumber],

      ["linkTo", "Link account to identifier"],

      // Authorization
      ["effectiveDate", "Requested effective date", notInPast],

      ["signer", "Authorized signer"],

      ["title", "Title"],

      ["signature", "Electronic signature"],

      ["signatureDate", "Signature date"],
    ];

    /*
     * Third-party agent fields
     */
    if (agentOn) {
      r.push(
        ["agentName", "Agent name"],

        ["agentPhone", "Agent phone", validators.phone],

        ["agentEmail", "Agent email", validators.email],
      );
    }

    return r;
  }, [agentOn]);

  /*
   * Optional fields
   */
  const optionalRules = useMemo(
    () => [
      ["fax", validators.phone],

      ["bankPhone", validators.phone],
    ],
    [],
  );

  /*
   * Get error for field
   */
  const errorFor = useCallback(
    (key) => {
      const rule = rules.find(([field]) => field === key);

      if (rule) {
        const [, label, validate] = rule;

        return (
          validators.required(f[key], label) ||
          (validate ? validate(f[key]) : null)
        );
      }

      const optional = optionalRules.find(([field]) => field === key);

      return optional ? optional[1](f[key]) : null;
    },
    [rules, optionalRules, f],
  );

  /*
   * Display error only after blur or submit
   */
  const err = (key) => (touched[key] || submitted ? errorFor(key) : null);

  /*
   * Load user's Tax IDs
   */
  const getUserTaxIds = async (userId) => {
    if (!isApiConfigured()) {
      return;
    }

    setPageLoading(true);

    try {
      const res = await userApi.getUsersTaxIds({
        userId,
      });

      if (res.success) {
        const taxIds = res?.data?.TaxIds || [];

        setUserTaxIds(taxIds);
      } else {
        setUserTaxIds([]);

        console.log(res.message);
      }
    } catch (err) {
      console.log("Error at getUserTaxIds : " + err);
    } finally {
      setPageLoading(false);
    }
  };

  /*  Get NPIs of particular TaxId selected   */
  const getNPIsOfSelectedTaxId = async (taxId) => {
    if (!isApiConfigured()) {
      return;
    }

    setPageLoading(true);

    try {
      const res = await eftEnrollmentApi.getNpiFromTaxId({
        taxId,
      });

      if (res.success) {
        const npis = res?.data || [];

        setNpisForSelectedTaxId(npis);

        /*
         * Automatically select first NPI
         * if available.
         */
        if (npis.length > 0) {
          const firstNpi = npis[0];

          setSelectedNpi(firstNpi);

          setF((previous) => ({
            ...previous,
            npi: firstNpi,
          }));
        }
      } else {
        setNpisForSelectedTaxId([]);

        console.log(res.message);
      }
    } catch (err) {
      console.log("Error at getNPIsOfSelectedTaxId : " + err);
    } finally {
      setPageLoading(false);
    }
  };

  const getEnrollmentByTaxId = async (taxId) => {
    if (!isApiConfigured()) {
      return;
    }

    setPageLoading(true);

    try {
      const res = await eftEnrollmentApi.getByTaxId({
        taxId,
        userId: providerAdminUserId,
      });

      if (res.success && res.data) {
        const enrollment = res.data;

        // Update NPI and Tax ID
        setSelectedNpi(enrollment.NPI || "");
        setSelectedTaxId(enrollment.TIN || "");

        //let linkToValue = enrollment?.LinkAccountTo?.trim() === enrollment?.NPI?.trim()
        //    ? `NPI—${enrollment?.NPI?.trim()}`
        //    : enrollment?.LinkAccountTo?.trim() === enrollment?.TIN?.trim()
        //        ? `TIN—${enrollment?.TIN?.trim()}`
        //        : '';

        const enrollmentTIN = enrollment.TIN?.trim() || "";
        const enrollmentNPI = enrollment.NPI?.trim() || "";
        const enrollmentLinkTo = enrollment.LinkAccountTo?.trim() || "";

        let linkToValue = "";

        if (enrollmentLinkTo === enrollmentNPI) {
          linkToValue = `NPI—${enrollmentNPI}`;
        } else if (enrollmentLinkTo === enrollmentTIN) {
          linkToValue = `TIN—${enrollmentTIN}`;
        }

        setEnrollmentId(enrollment.EFTEnrollmentId || "");

        setF((prev) => ({
          ...prev,

          // Provider
          legalName: enrollment.LegalName || "",
          dba: enrollment.DBA || "",
          tin: enrollment.TIN || "",
          npi: enrollment.NPI || "",
          contact: enrollment.ContactName || "",
          email: enrollment.Email || "",

          tradingPartnerId: enrollment.TradingPartnerId || "",

          phone: enrollment.Telephone || "",
          fax: enrollment.Fax || "",

          address: enrollment.StreetAddress || "",
          city: enrollment.City || "",
          state: enrollment.State || "",
          zip: enrollment.ZIP || "",

          // Third-party agent
          agentName: enrollment.AgentName || "",
          agentPhone: enrollment.AgentPhone || "",
          agentEmail: enrollment.AgentEmail || "",

          // EFT / Bank
          bankName: enrollment.BankName || "",
          routing: enrollment.RoutingNumber || "",
          accountType: enrollment.AccountType || "Checking",
          account: enrollment.AccountNumber || "",
          linkTo: linkToValue || "",
          //linkTo:
          //    enrollment?.LinkAccountTo?.trim() === enrollment?.NPI?.trim()
          //        ? `NPI—${enrollment?.NPI?.trim()}`
          //        : enrollment?.LinkAccountTo?.trim() === enrollment?.TIN?.trim()
          //            ? `TIN—${enrollment?.TIN?.trim()}`
          //            : '',
          bankPhone: enrollment.BankContactPhone || "",

          // Authorization
          //effectiveDate: enrollment.EffectiveDate
          //    ? enrollment.EffectiveDate.substring(0, 10)
          //    : '',

          effectiveDate: enrollment.EffectiveDate
            ? (() => {
                const date = new Date(enrollment.EffectiveDate);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");

                return `${year}-${month}-${day}`;
              })()
            : "",

          signer: enrollment.AuthorizedSigner || "",
          title: enrollment.Title || "",
          signature: enrollment.ElectronicSignature || "",

          //signatureDate: enrollment.SignatureDate
          //    ? enrollment.SignatureDate.substring(0, 10)
          //    : '',

          signatureDate: enrollment.SignatureDate
            ? (() => {
                const date = new Date(enrollment.SignatureDate);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");

                return `${year}-${month}-${day}`;
              })()
            : "",
          status: enrollment.Status || "",
          adminNote: enrollment.AdminNote || "",
        }));

        // setSelectedLinkTo(linkToValue);

        console.log("RECEIVED AND SET : " + JSON.stringify(f));
      } else {
        // No existing enrollment found
        // Reset form to default values

        setF(getDefaultFormValues());
        toast.warning(res.message, {
          title: "EFT Enrollment",
        });
        console.log(res.message);
      }
    } catch (err) {
      console.log("Error at getEnrollmentByTaxId : " + err);
    } finally {
      setPageLoading(false);
    }
  };
  useEffect(() => {
    if (!user?.userId) return;

    const loadTaxIds = async () => {
      if (
        user?.userId &&
        user?.userRole?.trim().toLowerCase() === "provider admin"
      ) {
        await getUserTaxIds(user.userId);
      }
    };

    loadTaxIds();
  }, [user?.userId]);

  useEffect(() => {
    if (selectedTaxId) {
      setF((previous) => ({
        ...previous,
        tin: selectedTaxId,
      }));
    }

    if (selectedNpi) {
      setF((previous) => ({
        ...previous,
        npi: selectedNpi,
      }));
    }
  }, [selectedTaxId, selectedNpi]);

  //useEffect(() => {

  //    if (
  //        selectedTaxId
  //    ) {

  //        setF((previous) => ({
  //            ...previous,

  //            tin: selectedTaxId,

  //            linkTo:
  //                previous.linkTo?.startsWith('TIN')
  //                    ? `TIN—${selectedTaxId}`
  //                    : previous.linkTo
  //        }));

  //    }

  //    if (
  //        selectedNpi
  //    ) {

  //        setF((previous) => ({
  //            ...previous,

  //            npi: selectedNpi,

  //            linkTo:
  //                previous.linkTo?.startsWith('NPI')
  //                    ? `NPI—${selectedNpi}`
  //                    : previous.linkTo
  //        }));

  //    }

  //}, [selectedTaxId, selectedNpi]);

  //useEffect(() => {

  //    if (selectedLinkTo)
  //    {

  //        //alert(selectedLinkTo);
  //        setF((previous) => ({
  //            ...previous,

  //            linkTo: selectedLinkTo
  //        }));

  //    }

  //}, [selectedLinkTo]);

  useEffect(() => {
    if (!id && !uid) return;

    const loadTaxIdData = async () => {
      setF((previous) => ({
        ...previous,
        tin: id,
      }));

      setProviderAdminUserId(uid);

      if (mode === "change") {
        // Fetch existing EFT enrollment
        // Populate form fields
        setReason("Change");

        await Promise.all([getUserTaxIds(uid), getNPIsOfSelectedTaxId(id)]);
      }
      if (mode === "newEnrollment") {
        // Fetch tax Id's of particular provider admin

        setReason("New enrollment");

        await Promise.all([getUserTaxIds(uid)]);
      }

      if (mode === "cancel") {
        // Fetch existing EFT enrollment
        // Show cancellation UI
        setReason("Cancel");
        //await getUserTaxIds(uid);
        //await getEnrollmentByTaxId(id);
        //await getNPIsOfSelectedTaxId(id);

        await Promise.all([
          getUserTaxIds(uid),
          //  getEnrollmentByTaxId(id),
          getNPIsOfSelectedTaxId(id),
        ]);
      }
    };

    loadTaxIdData();
  }, [id, mode, uid]);

  useEffect(() => {
    if (!id && !uid) return;

    const loadEnrollment = async () => {
      setF((previous) => ({
        ...previous,
        tin: id,
      }));

      setProviderAdminUserId(uid);

      if (
        (reason?.trim().toLowerCase() === "change" ||
          reason?.trim().toLowerCase() === "cancel") &&
        ["super admin", "admin"].includes(
          user?.userRole?.trim().toLowerCase(),
        ) &&
        id &&
        uid
      ) {
        // Fetch existing EFT enrollment
        // Populate form fields

        await getEnrollmentByTaxId(id);
      }
    };
    loadEnrollment();
  }, [reason]);

  /*
   * Submit EFT enrollment
   */
  const submit = async () => {
    setSubmitted(true);

    /*
     * Find first invalid field
     */
    const firstBad = [...rules, ...optionalRules]
      .map(([key]) => key)
      .find((key) => errorFor(key));

    if (firstBad) {
      setError("Please fill in all required fields.");

      const el = document.querySelector(`.enroll [data-field="${firstBad}"]`);

      el?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      el?.querySelector("input,select")?.focus({
        preventScroll: true,
      });

      return;
    }

    /*
     * Attestation
     */
    if (!agree) {
      setError("Please check the attestation before submitting.");

      return;
    }

    const today = getLocalDate();

    if (f.signatureDate !== today) {
      setError("Signature date must be today");
      return;
    }

    if (f.effectiveDate <= f.signatureDate) {
      setError("Effective date should be after signature date");
      return;
    }

    if (reason.trim().toLowerCase() === "new enrollment") {
      await submitNewEnrollment(f);
    } else if (reason.trim().toLowerCase() === "change") {
      await updateEnrollment({
        ...f,
        status: "Submitted",
      });
    } else if (reason.trim().toLowerCase() === "cancel") {
      await updateEnrollment({
        ...f,
        status: "Terminated",
      });
    } else {
      cosole.log("Oops! Something went wrong...");
    }

    setError("");

    //counter.current += 1;

    //const conf =
    //    `ENR-2026-000${counter.current}`;

    //const now =
    //    new Date()
    //        .toISOString()
    //        .slice(0, 16)
    //        .replace('T', ' ') +
    //    ' UTC';

    //const providerLine =
    //    `Provider: ${f.legalName || 'Your organization'}  ` +
    //    `(NPI ${f.npi || '—'}, TIN ${f.tin || '—'})`;

    //setReceipt(
    //    `Confirmation: ${conf}    Submitted: ${now}\n` +
    //    `Reason: ${reason}\n` +
    //    `${providerLine}\n` +
    //    `Bank details: account number masked on screen.\n\n` +
    //    `Status: Received — pending validation. ` +
    //    `A confirmation will be emailed to ` +
    //    `${f.email || 'your billing contact'}.`
    //);
  };

  const getUtcDateFromDateInput = (dateValue) => {
    if (!dateValue) return null;

    const [year, month, day] = dateValue.split("-").map(Number);

    const date = new Date();
    date.setUTCFullYear(year, month - 1, day);

    return date.toISOString();
  };

  const submitNewEnrollment = async (params) => {
    //alert(JSON.stringify(params));
    //return;
    if (!isApiConfigured()) {
      return;
    }

    setPageLoading(true);

    try {
      const res = await eftEnrollmentApi.create({
        ...params,
        reason: "New enrollment",
        userId: ["super admin", "admin"].includes(
          user?.userRole?.trim().toLowerCase(),
        )
          ? providerAdminUserId
          : user?.userId,
        attestation: true,
        // signatureDate: getUtcDateFromDateInput(params.signatureDate), //new (sent date to utc form)
        signatureDate: new Date().toISOString(), //new (sent date to utc form)

        effectiveDate: getUtcDateFromDateInput(params.effectiveDate), //new (sent date to utc form)
      });

      if (res.success && res.data) {
        toast.success(res.message, {
          title: "EFT Enrollment Submission",
        });

        const conf = res.data;

        const now =
          new Date().toISOString().slice(0, 16).replace("T", " ") + " UTC";

        const providerLine =
          `Provider: ${f.legalName || "Your organization"}  ` +
          `(NPI ${f.npi || "—"}, TIN ${f.tin || "—"})`;

        setReceipt(
          `Confirmation: ${conf}    Submitted: ${now}\n` +
            `Reason: ${reason}\n` +
            `${providerLine}\n` +
            `Bank details: account number masked on screen.\n\n` +
            `Status: Submitted — pending validation. ` +
            `A confirmation will be emailed to ` +
            `${f.email || "your billing contact"}.`,
        );
      } else {
        // console.log(res.message);
        toast.warning(res.message, {
          title: "EFT Enrollment Submission",
        });
      }
    } catch (err) {
      console.log("Error at submitNewEnrollment : " + err);

      const res = err?.response?.data;

      const errors = res?.Data?.errors;
      let message = res?.Message || "Validation failed";

      if (errors) {
        const firstError = Object.values(errors)
          .flat()
          .find((error) => error);

        if (firstError) {
          message = firstError;
        }
      }

      toast.warning(message, {
        title: "EFT Enrollment Submission",
      });
    } finally {
      setPageLoading(false);
    }
  };

  const updateEnrollment = async (params) => {
    //alert(JSON.stringify(params));
    //return;
    if (!isApiConfigured()) {
      return;
    }

    setPageLoading(true);

    try {
      const res = await eftEnrollmentApi.update({
        ...params,
        eFTEnrollmentId: enrollmentId,
        //reason: "change",
        reason: reason?.trim(), //perform both "Cancel" and "Change"

        //userId: providerAdminUserId,
        userId: ["super admin", "admin"].includes(
          user?.userRole?.trim().toLowerCase(),
        )
          ? providerAdminUserId
          : user?.userId,
        attestation: true,
        //signatureDate: getUtcDateFromDateInput(params.signatureDate), //new (sent date to utc form)
        signatureDate: new Date().toISOString(), //new (sent date to utc form)

        effectiveDate: getUtcDateFromDateInput(params.effectiveDate), //new (sent date to utc form)
      });

      if (res.success && res.data) {
        toast.success(res.message, {
          title: "EFT Enrollment Submission",
        });

        await getEnrollmentByTaxId(params.tin);
      } else {
        toast.warning(res.message, {
          title: "EFT Enrollment Submission",
        });
      }
    } catch (err) {
      console.log("Error at updateEnrollment : " + err);

      const res = err?.response?.data;

      const errors = res?.Data?.errors;
      let message = res?.Message || "Validation failed";

      if (errors) {
        const firstError = Object.values(errors)
          .flat()
          .find((error) => error);

        if (firstError) {
          message = firstError;
        }
      }

      toast.warning(message, {
        title: "EFT Enrollment Submission",
      });
    } finally {
      setPageLoading(false);
    }
  };

  const onBackToEFTEnrollmentList = () => {
    navigate(`/forms/eft-enrollment-list`);
  };

  return (
    <div className="enroll">
      {pageLoading && (
        <div style={styles.overlay}>
          <LoadingSpinner
            data-cy="page-loader-profile"
            message="Please wait..."
            fullPage="true"
          />
        </div>
      )}

      {/* =========================================================
          PAGE HEADER
      ========================================================= */}

      <div className="page-header">
        <div>
          <h1>EFT Enrollment</h1>

          <p
            style={{
              margin: "6px 0 0",
              color: "var(--text-secondary)",
              fontSize: 14,
            }}
          >
            Electronic Funds Transfer — receive claim payments by ACH direct
            deposit.
          </p>
        </div>
      </div>
      {user?.userRole?.trim().toLowerCase() === "super admin" && (
        <>
          {/* Back to EFT Enrollment list */}
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <button
              type="button"
                          onClick={onBackToEFTEnrollmentList}
              name="btnBackEFTList"
              style={{
                padding: "9px 16px",
                borderRadius: 8,
                border: "none",
                background: "#0D9488",
                color: "#fff",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                fontFamily: "inherit",
                boxShadow: "0 2px 5px rgba(13, 148, 136, 0.25)",
              }}
            >
              <ArrowLeft size={15} strokeWidth={2.5} />
              Go to Back
            </button>
          </div>
        </>
      )}

      {/* =========================================================
          REASON FOR SUBMISSION
      ========================================================= */}

      <div
        className="card"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "var(--text-secondary)",
          }}
        >
          Reason for submission
        </div>

        <div className="enroll-seg">
          {REASONS.map((item) => (
            <button
              key={item}
                  type="button"              
              className={item === reason ? "on" : ""}
              onClick={() => {
                setReason(item);
                setSelectedTaxId("");
                setSelectedNpi("");
                setReceipt("");
                setF(getDefaultFormValues());
              }}
            >
              {item}
            </button>
          ))}
        </div>

        <span
          style={{
            flex: 1,
          }}
        />

        <span className="enroll-legend">
          <span>
            <b
              style={{
                color: "var(--status-terminated,#DC2626)",
              }}
            >
              *
            </b>{" "}
            Required
          </span>

          <span>
            <b
              style={{
                color: "var(--status-pending,#B45309)",
              }}
            >
              ▲
            </b>{" "}
            Conditional
          </span>

          <span
            style={{
              color: "var(--text-tertiary,#9CA3AF)",
            }}
          >
            Unmarked = optional
          </span>
        </span>

        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "var(--text-secondary)",
          }}
        >
          {(reason?.trim().toLowerCase() === "change" ||
            reason?.trim().toLowerCase() === "cancel") && (
            <>
              <p>
                <span style={{ marginRight: "25px" }}>
                  Federal Tax ID (TIN/EIN): {f.tin || "N/A"}
                </span>

                <span style={{ marginRight: "25px" }}>
                  Status: {f.status || "N/A"}
                </span>

                <span>Note: {f.adminNote || "N/A"}</span>
              </p>
            </>
          )}
        </span>
      </div>

      {/* =========================================================
          1. PROVIDER INFORMATION
      ========================================================= */}

      <Section title="1 · Provider Information" note="Who is being paid">
        <F
          label="Federal Tax ID (TIN/EIN)"          
          tag="req"
          c={4}
          hint="9 digits"
          error={err("tin")}
          field="tin"
        >
          <select
                      value={selectedTaxId}
            name="selFederalId"
            onChange={async (e) => {
              const taxId = e.target.value;

              setSelectedTaxId(taxId);

              await getNPIsOfSelectedTaxId(taxId);

              setF((previous) => ({
                ...previous,
                tin: taxId,
              }));

              if (
                reason.trim().toLowerCase() === "change" ||
                reason.trim().toLowerCase() === "cancel"
              ) {
                //get and fill data of EFT enrollment
                await getEnrollmentByTaxId(taxId);
              }
            }}
          >
            <option value="">Select Federal Tax ID</option>

            {userTaxIds?.map((taxId) => (
              <option key={taxId} value={taxId}>
                {taxId.length === 9
                  ? `${taxId.slice(0, 2)}-${taxId.slice(2)}`
                  : taxId}
              </option>
            ))}
          </select>
        </F>
        <F
          label="Provider (legal) name"
          tag="req"
          c={4}
          error={err("legalName")}
          field="legalName"
        >
          <input
            {...bind("legalName")}
            placeholder="Legal name of the practice"
          />
        </F>

        <F label="Doing-business-as (DBA)" tag="opt" c={4}>
          <input {...bind("dba")} placeholder="DBA name, if any" />
        </F>

        {/*<F*/}
        {/*    label="NPI"*/}
        {/*    tag="req"*/}
        {/*    c={3}*/}
        {/*    hint="10 digits"*/}
        {/*    error={err('npi')}*/}
        {/*    field="npi"*/}
        {/*>*/}
        {/*    <input*/}
        {/*        inputMode="numeric"*/}
        {/*        {...bind(*/}
        {/*            'npi',*/}
        {/*            formatters.numeric*/}
        {/*        )}*/}
        {/*        maxLength={10}*/}
        {/*        placeholder="1234567893"*/}
        {/*    />*/}
        {/*</F>*/}

        <F
          label="NPI"
          tag="req"
          c={3}
          hint="10 digits"
          error={err("npi")}
          field="npi"
        >
          <select
            value={selectedNpi}
            onChange={(e) => {
              const npi = e.target.value;

              setSelectedNpi(npi);

              setF((previous) => ({
                ...previous,
                npi: npi,
              }));
            }}
            disabled={!selectedTaxId}
          >
            <option value="">
              {selectedTaxId ? "Select NPI" : "Select Tax ID first"}
            </option>

            {npisForSelectedTaxId?.map((npi) => (
              <option key={npi} value={npi}>
                {npi}
              </option>
            ))}
          </select>
        </F>

        <F
          label="Contact name"
          tag="req"
          c={5}
          error={err("contact")}
          field="contact"
        >
          <input
            {...bind("contact")}
            placeholder="A. Morales, Billing Manager"
          />
        </F>

        <F label="Email" tag="req" c={4} error={err("email")} field="email">
          <input
            type="email"
            {...bind("email")}
            placeholder="billing@clinic.example"
          />
        </F>

        <F label="Trading Partner ID" tag="opt" c={3}>
          <input {...bind("tradingPartnerId")} placeholder="4137147" />
        </F>

        <F label="Telephone" tag="req" c={3} error={err("phone")} field="phone">
          <input
            inputMode="tel"
            {...bind("phone", formatters.phone)}
            placeholder="(555) 010-2233"
          />
        </F>

        <F label="Fax" tag="opt" c={3} error={err("fax")} field="fax">
          <input
            inputMode="tel"
            {...bind("fax", formatters.phone)}
            placeholder="(555) 010-2234"
          />
        </F>

        <F
          label="Street address"
          tag="req"
          c={5}
          error={err("address")}
          field="address"
        >
          <input {...bind("address")} placeholder="4080 SE Gatehouse" />
        </F>

        <F label="City" tag="req" c={3} error={err("city")} field="city">
          <input {...bind("city")} placeholder="Austin" />
        </F>

        <F label="State" tag="req" c={2} error={err("state")} field="state">
          <input
            {...bind("state", formatters.state)}
            maxLength={2}
            placeholder="TX"
          />
        </F>

        <F label="ZIP+4" tag="req" c={2} error={err("zip")} field="zip">
          <input
            inputMode="numeric"
            {...bind("zip", formatters.zip)}
            placeholder="78702-0000"
          />
        </F>

        {/* Third party agent */}

        <div
          className="enroll-subrow"
          style={{
            gridColumn: "span 12",
          }}
        >
          <label className="enroll-check">
            <input
              type="checkbox"
              checked={agentOn}
              onChange={(e) => setAgentOn(e.target.checked)}
            />
            A third-party agent is submitting on the provider's behalf
          </label>
        </div>

        {agentOn && (
          <>
            <F
              label="Agent name"
              tag="cond"
              c={6}
              error={err("agentName")}
              field="agentName"
            >
              <input
                {...bind("agentName")}
                placeholder="Billing service name"
              />
            </F>

            <F
              label="Agent phone"
              tag="cond"
              c={3}
              error={err("agentPhone")}
              field="agentPhone"
            >
              <input
                inputMode="tel"
                {...bind("agentPhone", formatters.phone)}
                placeholder="(555) 777-0100"
              />
            </F>

            <F
              label="Agent email"
              tag="cond"
              c={3}
              error={err("agentEmail")}
              field="agentEmail"
            >
              <input
                type="email"
                {...bind("agentEmail")}
                placeholder="ops@billing.example"
              />
            </F>
          </>
        )}
      </Section>

      {/* =========================================================
          2. FINANCIAL INSTITUTION
      ========================================================= */}

      <Section title="2 · Financial Institution" note="Where deposits go">
        <F
          label="Bank name"
          tag="req"
          c={5}
          error={err("bankName")}
          field="bankName"
        >
          <input {...bind("bankName")} placeholder="First National Bank" />
        </F>

        <F
          label="Routing number (ABA)"
          tag="req"
          c={3}
          hint="9 digits · ABA checksum"
          error={err("routing")}
          field="routing"
        >
          <input
            inputMode="numeric"
            autoComplete="off"
            maxLength={9}
            {...bind("routing", formatters.numeric)}
            placeholder="091000019"
          />
        </F>

        <F
          label="Account type"
          tag="req"
          c={4}
          error={err("accountType")}
          field="accountType"
        >
          <select {...bind("accountType")}>
            <option>Checking</option>

            <option>Savings</option>
          </select>
        </F>

        <F
          label="Account number"
          tag="req"
          c={5}
          hint="Masked on screen"
          error={err("account")}
          field="account"
        >
          <div
            style={{
              position: "relative",
            }}
          >
            <input
              type={showAcct ? "text" : "password"}
              inputMode="numeric"
              autoComplete="off"
              maxLength={17}
              {...bind("account", formatters.numeric)}
              placeholder="•••• •••• 6789"
              style={{
                paddingRight: 60,
              }}
            />

            <button
              type="button"
              className="enroll-inbtn"
              onClick={() => setShowAcct((value) => !value)}
            >
              {showAcct ? "Hide" : "Show"}
            </button>
          </div>
        </F>

        <F
          label="Link account to identifier"
          tag="req"
          c={4}
          hint="CORE reassociation linkage"
          error={err("linkTo")}
          field="linkTo"
        >
          <select
            value={f.linkTo ?? ""}
            onChange={(e) => {
              setF((prev) => ({
                ...prev,
                linkTo: e.target.value,
              }));
            }}
          >
            <option value="">Select identifier</option>

            {linkOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </F>

        <F
          label="Bank contact phone"
          tag="opt"
          c={3}
          error={err("bankPhone")}
          field="bankPhone"
        >
          <input
            inputMode="tel"
            {...bind("bankPhone", formatters.phone)}
            placeholder="(800) 555-1212"
          />
        </F>
      </Section>

      {/* =========================================================
          3. SUBMISSION & AUTHORIZATION
      ========================================================= */}

      <Section title="3 · Submission & Authorization">
        <F
          label="Requested effective date"
          tag="req"
          c={3}
          error={err("effectiveDate")}
          field="effectiveDate"
        >
          <input type="date" {...bind("effectiveDate")} />
        </F>

        <F
          label="Authorized signer (printed)"
          tag="req"
          c={5}
          error={err("signer")}
          field="signer"
        >
          <input
            {...bind("signer")}
            placeholder="Full name of authorized signer"
          />
        </F>

        <F label="Title" tag="req" c={4} error={err("title")} field="title">
          <input {...bind("title")} placeholder="Practice Administrator" />
        </F>

        <F
          label="Electronic signature"
          tag="req"
          c={8}
          error={err("signature")}
          field="signature"
        >
          <input {...bind("signature")} placeholder="Type full name to sign" />
        </F>

        <F
          label="Signature date"
          tag="req"
          c={4}
          error={err("signatureDate")}
          field="signatureDate"
        >
          <input type="date" {...bind("signatureDate")} />
        </F>
      </Section>

      {/* =========================================================
          SUBMIT
      ========================================================= */}

      <div
        className="card"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <label
          className={`enroll-attest${submitted && !agree ? " has-error" : ""}`}
        >
          <input
                      type="checkbox"
            name="chkAttestation"
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
          />

          <span>
            I certify the information is accurate and I am authorized to enroll
            this provider for EFT. I authorize deposits to the account above.
            {submitted && !agree && (
              <span
                className="enroll-error"
                style={{
                  display: "block",
                  marginTop: 4,
                }}
              >
                <AlertCircle size={13} strokeWidth={2.2} />
                Attestation is required
              </span>
            )}
          </span>
        </label>

        <span
          style={{
            flex: 1,
          }}
        />

        <button type="button" className="btn-primary" onClick={submit}>
          Submit enrollment
        </button>
      </div>

      {error && (
        <div
          className="error-text"
          style={{
            color: "var(--status-terminated,#DC2626)",
            fontSize: 13,
            fontWeight: 600,
            marginTop: -8,
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <AlertCircle size={15} strokeWidth={2.2} />

          {error}
        </div>
      )}

      {/* =========================================================
          RECEIPT
      ========================================================= */}

      {receipt && (
        <div
          className="card"
          style={{
            borderColor: "var(--status-active,#10B981)",
            background: "var(--status-active-bg,#D1FAE5)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontWeight: 700,
              fontSize: 15,
              color: "#047857",
              marginBottom: 10,
            }}
          >
            <Check size={18} strokeWidth={2.6} />
            EFT enrollment received
          </div>

          <pre
            style={{
              margin: 0,
              fontFamily: "ui-monospace, Menlo, monospace",
              fontSize: 12.5,
              whiteSpace: "pre-wrap",
              color: "var(--text-primary)",
            }}
          >
            {receipt}
          </pre>
        </div>
      )}

      {/* =========================================================
          HOW IT WORKS
      ========================================================= */}

      <div className="card">
        <div className="enroll-sec-head">
          <h3>How enrollment works</h3>
        </div>

        <div className="enroll-steps">
          {[
            [
              "Choose a reason",
              "New enrollment, Change (update existing details), or Cancel.",
            ],

            [
              "Enter provider info",
              "Legal name, Tax ID, NPI, and a billing contact — where confirmations are sent.",
            ],

            [
              "Add banking details",
              "Bank, routing, account type/number, and the NPI or TIN the account links to. The account number is masked on screen.",
            ],

            [
              "Sign and submit",
              "An authorized person e-signs. You get a confirmation number now; a second notice follows when it is active.",
            ],
          ].map(([heading, description], index) => (
            <div key={index} className="enroll-step">
              <span className="n">{index + 1}</span>

              <div>
                <b>{heading}</b>

                <div>{description}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="enroll-note">
          Enroll in ERA too so payments and remittances auto-reassociate. Need
          help? Reach Provider Services via Contact & Resources.
        </div>
      </div>

      {/* =========================================================
          STYLES
      ========================================================= */}

      <style>{`

        .enroll .enroll-grid {
          display: grid;
          grid-template-columns:
            repeat(12, minmax(0, 1fr));
          gap: 16px 20px;
        }

        .enroll .enroll-fg {
          display: flex;
          flex-direction: column;
          gap: 6px;
          min-width: 0;
        }

        .enroll .enroll-fg > label {
          font-size: 13px;
          font-weight: 600;
          color:
            var(--text-secondary,#6B7280);
          letter-spacing: .01em;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .enroll .enroll-req {
          color:
            var(--status-terminated,#DC2626);
          font-weight: 800;
          font-size: 15px;
          line-height: 1;
        }

        .enroll .enroll-cond {
          color:
            var(--status-pending,#B45309);
          font-size: 10px;
          line-height: 1;
        }

        .enroll .enroll-hint {
          font-size: 11.5px;
          color:
            var(--text-tertiary,#9CA3AF);
          font-family:
            ui-monospace,Menlo,monospace;
        }

        .enroll .enroll-error {
          font-size: 12px;
          color: #EF4444;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .enroll input,
        .enroll select {
          width: 100%;
          background:
            var(--bg-input,#F8FAFB);
          border:
            1px solid
            var(--border-color,#E8ECF0);
          border-radius:
            var(--radius-sm,10px);
          padding: 10px 12px;
          font: inherit;
          font-size: 14px;
          color:
            var(--text-primary,#1A1D21);
          outline: none;
          transition:
            border-color .15s,
            background .15s;
        }

        .enroll input:focus,
        .enroll select:focus {
          border-color:
            var(--primary,#0D9488);
          background: #fff;
        }

        .enroll .has-error input,
        .enroll .has-error select {
          border-color: #EF4444;
          background: #FEF2F2;
        }

        .enroll .has-error input:focus,
        .enroll .has-error select:focus {
          border-color: #EF4444;
          box-shadow:
            0 0 0 3px
            rgba(239,68,68,.12);
        }

        .enroll input[type=checkbox] {
          width: auto;
        }

        .enroll .enroll-sec-head {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 0 0 18px;
          padding-bottom: 12px;
          border-bottom:
            1px solid
            var(--border-color,#E8ECF0);
        }

        .enroll .enroll-sec-head h3 {
          margin: 0;
          font-size: 15px;
          font-weight: 700;
          color:
            var(--text-primary,#1A1D21);
        }

        .enroll .enroll-sec-head span {
          margin-left: auto;
          font-size: 12.5px;
          color:
            var(--text-tertiary,#9CA3AF);
        }

        .enroll .enroll-seg {
          display: inline-flex;
          background:
            var(--bg-subtle,#F1F5F9);
          border:
            1px solid
            var(--border-color,#E8ECF0);
          border-radius: 10px;
          padding: 3px;
        }

        .enroll .enroll-seg button {
          border: 0;
          background: transparent;
          font: inherit;
          font-size: 13px;
          font-weight: 600;
          color:
            var(--text-secondary,#6B7280);
          padding: 7px 15px;
          border-radius: 7px;
          cursor: pointer;
        }

        .enroll .enroll-seg button.on {
          background:
            var(
              --gradient-primary,
              linear-gradient(
                135deg,
                #0F766E,
                #0D9488
              )
            );
          color: #fff;
        }

        .enroll .enroll-legend {
          display: flex;
          gap: 14px;
          font-size: 12px;
          font-weight: 600;
        }

        .enroll .enroll-subrow {
          border-top:
            1px dashed
            var(--border-color,#E8ECF0);
          padding-top: 14px;
        }

        .enroll .enroll-check,
        .enroll .enroll-attest {
          display: flex;
          align-items: center;
          gap: 9px;
          font-size: 13.5px;
          color:
            var(--text-primary,#1A1D21);
          cursor: pointer;
        }

        .enroll .enroll-attest {
          align-items: flex-start;
          max-width: 620px;
          color:
            var(--text-secondary,#6B7280);
        }

        .enroll .enroll-attest.has-error {
          color: #B91C1C;
        }

        .enroll .enroll-inbtn {
          position: absolute;
          right: 6px;
          top: 50%;
          transform:
            translateY(-50%);
          border: 0;
          background:
            var(--bg-subtle,#F1F5F9);
          color:
            var(--text-secondary,#6B7280);
          font: inherit;
          font-size: 12px;
          font-weight: 600;
          padding: 5px 10px;
          border-radius: 6px;
          cursor: pointer;
        }

        .enroll .enroll-steps {
          display: grid;
          grid-template-columns:
            repeat(
              auto-fit,
              minmax(240px,1fr)
            );
          gap: 16px;
        }

        .enroll .enroll-step {
          display: flex;
          gap: 11px;
          align-items: flex-start;
        }

        .enroll .enroll-step .n {
          flex: none;
          width: 24px;
          height: 24px;
          border-radius: 7px;
          background:
            rgba(13,148,136,.1);
          color:
            var(--primary-dark,#0F766E);
          display: grid;
          place-items: center;
          font-size: 12.5px;
          font-weight: 800;
        }

        .enroll .enroll-step b {
          font-size: 13.5px;
          color:
            var(--text-primary,#1A1D21);
        }

        .enroll .enroll-step div div {
          font-size: 12.5px;
          color:
            var(--text-secondary,#6B7280);
          margin-top: 2px;
          line-height: 1.5;
        }

        .enroll .enroll-note {
          margin-top: 16px;
          padding: 11px 15px;
          border-left:
            3px solid
            var(--primary,#0D9488);
          background:
            var(--bg-subtle,#F1F5F9);
          border-radius:
            0 8px 8px 0;
          font-size: 13px;
          color:
            var(--text-secondary,#6B7280);
        }

        @media (max-width:860px) {

          .enroll .enroll-grid {
            grid-template-columns:
              repeat(2,1fr);
          }

          .enroll .enroll-grid > * {
            grid-column:
              span 2 !important;
          }

        }

      `}</style>
    </div>
  );
}

/*
 * Page loading overlay
 */
const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(255,255,255,0.7)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};
